import React, { ReactElement, useEffect, useState } from 'react';
import { Box, Button, Dialog, IconButton, Stack, Typography } from '@mui/material';
import { Close } from '@mui/icons-material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';

import {
  gray40,
  gray80,
  gray100,
  orange50,
  primaryButtonBackgroundColor,
  tertiaryButtonBackgroundColor,
} from '../../../constants/colors';
import { boldMd, regularLg, regularMd, regularXl } from '../../../constants/location';

export interface MasterHistoryItem {
  label: string;
  ocrValue: string;
  matchedName: string;
  /** 照合履歴と一緒に記録する項目の値（取引先コードなど）。設定時のみ表示する。 */
  supplierValue?: string;
  /** 上記項目の名称（例：取引先コード）。表示用。 */
  supplierLabel?: string;
}

type Props = {
  open: boolean;
  onClose: () => void;
  onSkip: () => void;
  /** 選択された項目のインデックス配列を渡して保存する */
  onConfirm: (selectedIndexes: number[]) => void;
  items: MasterHistoryItem[];
  loading?: boolean;
};

const styles = {
  buttonWrapper: {
    display: 'flex',
    gap: '16px',
    marginTop: '32px',
    '@media screen and (max-width:960px)': {
      gap: '8px',
      marginTop: '20px',
    },
  },
  cancelButton: {
    ...tertiaryButtonBackgroundColor,
    height: '44px',
    width: 'calc(50% - 8px)',
    '@media screen and (max-width:960px)': {
      width: 'calc(50% - 4px)',
    },
  },
  cancelButtonText: {
    ...boldMd,
    color: gray80,
  },
  confirmButton: {
    height: '44px',
    width: 'calc(50% - 8px)',
    ...primaryButtonBackgroundColor,
  },
  confirmButtonText: {
    ...boldMd,
    color: 'white',
  },
  dialog: {
    '& .MuiPaper-root': {
      border: '1px solid' + gray40,
      boxSizing: 'border-box',
      margin: '16px',
      padding: '40px',
      '@media screen and (max-width: 960px)': {
        padding: '32px 16px',
      },
    },
  },
  header: {
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: '24px',
    '@media screen and (max-width:960px)': {
      marginBottom: '16px',
    },
  },
  icon: {
    height: '32px',
    width: '32px',
    '@media screen and (max-width:960px)': {
      height: '24px',
      width: '24px',
    },
  },
  iconButton: {
    padding: 0,
  },
  title: {
    ...regularXl,
    '@media screen and (max-width:960px)': {
      ...regularLg,
    },
  },
  description: {
    ...regularMd,
    marginBottom: '16px',
  },
  selectAllButton: {
    ...tertiaryButtonBackgroundColor,
    height: '36px',
  },
  itemList: {
    maxHeight: 'calc(100vh - 360px)',
    overflowY: 'auto' as const,
    paddingRight: '4px',
  },
  // クリックで選択/解除する行カード（選択時は青枠・薄青背景）。
  itemBox: {
    borderRadius: '4px',
    border: `1px solid ${gray40}`,
    marginBottom: '8px',
    padding: '12px 16px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'flex-start',
    gap: '12px',
  },
  itemBody: {
    flex: 1,
    minWidth: 0,
  },
  itemLabel: {
    ...boldMd,
    // boldMd はオレンジ文字のため、本文として黒系で上書きする。
    color: gray100,
    marginBottom: '4px',
  },
  itemRow: {
    ...regularMd,
    color: gray80,
  },
};

const MasterHistorySaveConfirmModal = ({
  open,
  onClose,
  onSkip,
  onConfirm,
  items,
  loading = false,
}: Props): ReactElement => {
  // 選択中のインデックス集合。初期は全選択。
  const [selectedIndexes, setSelectedIndexes] = useState<Set<number>>(
    () => new Set(items.map((_, i) => i)),
  );

  // モーダルを開くたび（items 変更時）に全選択へリセットする。
  useEffect(() => {
    if (open) {
      setSelectedIndexes(new Set(items.map((_, i) => i)));
    }
  }, [open, items]);

  const toggle = (index: number): void => {
    setSelectedIndexes(prev => {
      const next = new Set(prev);
      if (next.has(index)) {
        next.delete(index);
      } else {
        next.add(index);
      }
      return next;
    });
  };

  const handleSelectAll = (): void => {
    setSelectedIndexes(new Set(items.map((_, i) => i)));
  };

  const handleDeselectAll = (): void => {
    setSelectedIndexes(new Set());
  };

  const handleConfirm = (): void => {
    const indexes = items.map((_, i) => i).filter(i => selectedIndexes.has(i));
    onConfirm(indexes);
  };

  const selectedCount = selectedIndexes.size;

  return (
    <Dialog open={open} sx={styles.dialog}>
      <Box sx={styles.header}>
        <Typography sx={styles.title}>照合結果を履歴として保存</Typography>
        <IconButton onClick={onClose} sx={styles.iconButton} disabled={loading}>
          <Close sx={styles.icon} />
        </IconButton>
      </Box>
      <Typography sx={styles.description}>
        照合履歴として保存する照合結果を選択してください。
      </Typography>

      <Stack direction="row" spacing={1} sx={{ marginBottom: '16px' }}>
        <Button sx={styles.selectAllButton} onClick={handleSelectAll} disabled={loading}>
          <Typography sx={{ ...boldMd, color: gray80 }}>すべて選択</Typography>
        </Button>
        <Button sx={styles.selectAllButton} onClick={handleDeselectAll} disabled={loading}>
          <Typography sx={{ ...boldMd, color: gray80 }}>すべて解除</Typography>
        </Button>
      </Stack>

      <Box sx={styles.itemList}>
        {items.map((item, index) => {
          const isSelected = selectedIndexes.has(index);
          return (
            <Box
              key={index}
              sx={{
                ...styles.itemBox,
                borderColor: isSelected ? orange50 : gray40,
              }}
              onClick={(): void => toggle(index)}
            >
              {isSelected ? (
                <CheckCircleIcon sx={{ color: orange50, fontSize: '22px' }} />
              ) : (
                <RadioButtonUncheckedIcon sx={{ color: gray40, fontSize: '22px' }} />
              )}
              <Box sx={styles.itemBody}>
                <Typography sx={styles.itemLabel}>{item.label}</Typography>
                <Typography sx={styles.itemRow}>読取結果の値：{item.ocrValue}</Typography>
                <Typography sx={styles.itemRow}>マスタの値：{item.matchedName}</Typography>
                {item.supplierValue && (
                  <Typography sx={styles.itemRow}>
                    {item.supplierLabel ? `${item.supplierLabel}の値` : '記録する項目の値'}：
                    {item.supplierValue}
                  </Typography>
                )}
              </Box>
            </Box>
          );
        })}
      </Box>

      <Box sx={styles.buttonWrapper}>
        <Button sx={styles.cancelButton} onClick={onSkip} disabled={loading}>
          <Typography sx={styles.cancelButtonText}>保存しない</Typography>
        </Button>
        <Button
          sx={styles.confirmButton}
          onClick={handleConfirm}
          disabled={loading || selectedCount === 0}
        >
          <Typography sx={styles.confirmButtonText}>
            {loading ? '保存中...' : `選択した${selectedCount}件を保存`}
          </Typography>
        </Button>
      </Box>
    </Dialog>
  );
};

export default MasterHistorySaveConfirmModal;
